/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React from 'react';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';
import { parse } from '../../../../util/urlHelpers';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../../util/reactIntl';
import { ensureCurrentUser } from '../../../../util/data';

import {
  AvatarLarge,
  ExternalLink,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
  IconArrowHead,
  Button,
  IconClose,
} from '../../../../components';

import css from './TopbarMobileMenu.module.css';

const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { group, text, type, href, route } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(css.navigationLink, getCurrentPageClass(name));
    return (
      <NamedLink name={name} params={params} to={to} className={className}>
        <span className={css.menuItemBorder} />
        {text}
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={css.navigationLink}>
      <span className={css.menuItemBorder} />
      {text}
    </ExternalLink>
  );
};

/**
 * Menu for mobile layout (opens through hamburger icon)
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @param {string?} props.currentPage
 * @param {Object?} props.currentUser API entity
 * @param {number} props.notificationCount
 * @param {Array<Object>} props.customLinks Contains object like { group, text, type, href, route }
 * @param {Function} props.onLogout
 * @param {Function} props.onClose
 * @returns {JSX.Element} search icon
 */
const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    currentUser,
    notificationCount = 0,
    salesNotificationCount = 0,
    ordersNotificationCount = 0,
    customLinks,
    onLogout,
    onClose,
  } = props;

  const user = ensureCurrentUser(currentUser);
  const location = useLocation();
  const queryParams = parse(location.search);
  const isCategoryActive = categoryValue => {

    const activeCategory = queryParams.pub_level2;
    // It's active if we are on SearchPage and the category value matches the URL
    return currentPage === 'SearchPage' && activeCategory === categoryValue;
  }
  const isAllListingsActive = currentPage === 'SearchPage' && !queryParams.pub_level2;

  const CategoryLinks = ({ isAuthenticated = true }) => (
    <div className={css.categoryLinksWrapper}>
      {[
        { id: 'baseballcards', value: 'baseballcards,rawbaseball', labelId: 'TopbarMobileMenu.categoryBaseball' },
        { id: 'basketballcards', value: 'basketballcards,rawbasketball', labelId: 'TopbarMobileMenu.categoryBasketball' },
        { id: 'footballcards', value: 'footballcards,rawfootball', labelId: 'TopbarMobileMenu.categoryFootball' },
        { id: 'soccercards', value: 'soccercards,rawsoccer', labelId: 'TopbarMobileMenu.categorySoccer' },
        { id: 'hockeycards', value: 'hockeycards,rawhockey', labelId: 'TopbarMobileMenu.categoryHockey' },
        { id: 'wrestlingcards', value: 'wrestlingcards,rawwrestling', labelId: 'TopbarMobileMenu.categoryWrestling' },
        { id: 'misccards', value: 'misccards,rawmisccards', labelId: 'TopbarMobileMenu.categoryMisc' },
      ].map(cat => (
        <NamedLink
          key={cat.id}
          name="SearchPage"
          to={{ search: `?pub_level2=${cat.value}` }}
          className={classNames(
            css.navigationLink,
            css.categoryLink,
            !isAuthenticated && css.categoryLinkUnauthenticated,
            isCategoryActive(cat.value) ? css.currentPage : null
          )}
        >
          <span className={css.categoryText}>
            <FormattedMessage id={cat.labelId} />
          </span>
          <IconArrowHead className={css.categoryArrow} direction="right" size="small" />
        </NamedLink>
      ))}
      <NamedLink
        name="SearchPage"
        className={classNames(
          css.navigationLink,
          css.categoryLink,
          !isAuthenticated && css.categoryLinkUnauthenticated,
          isAllListingsActive ? css.currentPage : null
        )}
      >
        <span className={css.categoryText}>
          <FormattedMessage id="TopbarMobileMenu.allListingsLink" />
        </span>
        <IconArrowHead
          className={css.categoryArrow}
          direction="right"
          size="small"
        />
      </NamedLink>
    </div>
  );

  const ActionButtons = () => (
    <div className={css.actionButtons}>
      {!isAuthenticated && (
        <>
          <NamedLink name="SignupPage" className={css.sellNowBtn}>
            <FormattedMessage id="TopbarMobileMenu.sellNow" />
          </NamedLink>
          <NamedLink name="SignupPage" className={css.authBtn}>
            <FormattedMessage id="TopbarMobileMenu.signupLink" />
          </NamedLink>
          <NamedLink name="LoginPage" className={css.authBtn}>
            <FormattedMessage id="TopbarMobileMenu.loginLink" />
          </NamedLink>
        </>
      )}
    </div>
  );

  const extraLinks = customLinks.map((linkConfig, index) => {
    return (
      <CustomLinkComponent
        key={`${linkConfig.text}_${index}`}
        linkConfig={linkConfig}
        currentPage={currentPage}
      />
    );
  });

  if (!isAuthenticated) {
    const signup = (
      <NamedLink name="SignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage id="TopbarMobileMenu.signupOrLogin" values={{ signup, login }} />
      </span>
    );
    return (
      <div className={css.root}>
        <Button
          rootClassName={css.closeButton}
          onClick={onClose}
          title="Close menu"
        >
          <IconClose rootClassName={css.closeIcon} />
        </Button>
        <div className={classNames(css.content, css.unauthenticatedContent)}>
          <img src={`${process.env.REACT_APP_MARKETPLACE_ROOT_URL}/static/assets/cardMarketPlaceLogo.png`}
            alt={'The card marketplace logo'}
            className={css.logo}></img>
          {/* <div className={css.authenticationGreeting}>
            <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreeting"
              values={{ lineBreak: <br />, signupOrLogin }}
            />
          </div> */}
          <ActionButtons />

          <div className={css.menuSection}>
            <CategoryLinks isAuthenticated={false} />
            {/* <div className={css.customLinksWrapper}>{extraLinks}</div> */}
          </div>

          {/* <div className={css.spacer} /> */}
        </div>
        {/* <div className={css.footer}>
          <NamedLink className={css.createNewListingLink} name="NewListingPage">
            <FormattedMessage id="TopbarMobileMenu.newListingLink" />
          </NamedLink>
        </div> */}
      </div>
    );
  }

  const isInboxPage = currentPage?.indexOf('InboxPage') === 0;
  const currentTab = isInboxPage ? currentPage.split(':')[1] : null;

  let effectiveCount = 0;
  if (isInboxPage) {
    if (currentTab === 'sales') {
      effectiveCount = salesNotificationCount;
    } else if (currentTab === 'orders') {
      effectiveCount = ordersNotificationCount;
    }
  } else {
    effectiveCount = notificationCount;
  }

  const notificationCountBadge =
    effectiveCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={effectiveCount} />
    ) : null;

  const displayName = user.attributes.profile.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage ? css.currentPage : null;
  };
  return (
    <div className={css.root}>
      <Button
        rootClassName={css.closeButton}
        onClick={onClose}
        title="Close menu"
      >
        <IconClose rootClassName={css.closeIcon} />
      </Button>
      {/* <AvatarLarge className={css.avatar} user={currentUser} /> */}
      <div className={css.content}>
        <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span>
        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>

        <div className={css.accountLinksWrapper}>
          <CategoryLinks isAuthenticated={true} />
          <hr className={css.divider} />
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ProfilePage'))}
            name="ProfilePage"
            params={{ id: currentUser.id.uuid }}
          >
            <FormattedMessage id="TopbarMobileMenu.yourProfile" />
          </NamedLink>
          <NamedLink
            className={classNames(css.inbox, currentPageClass('DashboardPage'))}
            name="DashboardPage"
          >
            <FormattedMessage id="TopbarMobileMenu.inboxLink" />
            {notificationCountBadge}
          </NamedLink>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('EditListingPage'))}
            name="NewListingPage"
          >
            <FormattedMessage id="TopbarMobileMenu.newListingLink" />
          </NamedLink>
          {/* <NamedLink name="SearchPage" className={classNames(
            css.navigationLink,
            isAllListingsActive ? css.currentPage : null
          )}>
            <FormattedMessage id="TopbarMobileMenu.allListingsLink" />
          </NamedLink> */}
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}
            name="ManageListingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
          </NamedLink>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('WishlistPage'))}
            name="WishlistPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.yourWishlistLink" />
          </NamedLink>
          <NamedLink className={classNames(css.navigationLink, currentPageClass('HistoryPage'))} name="HistoryPage">
            <FormattedMessage id="TopbarMobileMenu.buyingSellingHistory" />
          </NamedLink>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
          </NamedLink>

          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
          </NamedLink>


        </div>
        {/* <div className={css.customLinksWrapper}>{extraLinks}</div> */}
        {/* <div className={css.spacer} /> */}
      </div>
      {/* <div className={css.footer}>
        <NamedLink className={css.createNewListingLink} name="NewListingPage">
          <FormattedMessage id="TopbarMobileMenu.newListingLink" />
        </NamedLink>
      </div> */}
    </div>
  );
};

export default TopbarMobileMenu;
